/**
 * Conversion helpers between the Firestore delivery-step condition JSON shape
 * (`{ logic, conditions: [...] }`, used by both `run_when` and `preconditions`)
 * and the QueryBuilder tree (`{ type, id, conjunction, children }`). Shared by
 * the Track Status and Text Notes tabs.
 *
 * Operator values are kept identical on both sides (see deliveryStepFields.ts),
 * so conversion only restructures the tree and shuffles `values`/`value`.
 *
 * Note: the design mockup renders a `stale_in` rule as a nested
 * `(field is any of […]) AND (Is Stale = TRUE)` group. We instead keep it as a
 * single `stale_in` condition so the selected values round-trip without a
 * synthetic field; the stale window is carried via `stale_after_minutes`.
 */
import { v4 as uuidv4 } from "uuid";
import { createEmptyGroup } from "../../../../../components/queryBuilder/queryBuilderUtils";
import {
  DEFAULT_STALE_AFTER_MINUTES,
  DeliveryConditionNode,
  DeliveryRunWhenCondition,
  DeliveryRunWhenGroup,
  isDeliveryConditionGroup,
} from "../../../../../data-model/deliveryStepsConfig";
import {
  QueryCondition,
  QueryGroup,
  QueryNode,
} from "../../../../../data-model/queryBuilder";

const EMPTY_VALUE_OPERATORS = new Set(["is_empty", "is_not_empty"]);

/** Coerces a stored condition value to the string | string[] the builder expects. */
function toQueryValue(cond: DeliveryRunWhenCondition): string | string[] {
  if (Array.isArray(cond.values)) {
    return cond.values.map((v) => String(v));
  }
  if (cond.value != null) {
    return String(cond.value);
  }
  return "";
}

function conditionToQueryNode(cond: DeliveryRunWhenCondition): QueryCondition {
  return {
    type: "condition",
    id: cond.id ?? uuidv4(),
    field: cond.field ?? "",
    operator: cond.operator ?? "",
    value: toQueryValue(cond),
  };
}

/**
 * Converts a Firestore condition group into a QueryBuilder value tree.
 *
 * @param group - Firestore run_when / preconditions group (may be undefined)
 * @returns QueryBuilder root group; an empty group when no conditions exist
 */
export function runWhenToQueryValue(
  group: DeliveryRunWhenGroup | undefined,
): QueryGroup {
  if (!group || !Array.isArray(group.conditions)) {
    return createEmptyGroup();
  }
  return {
    type: "group",
    id: uuidv4(),
    conjunction: group.logic === "AND" ? "AND" : "OR",
    children: group.conditions.map((node) =>
      isDeliveryConditionGroup(node)
        ? runWhenToQueryValue(node)
        : conditionToQueryNode(node),
    ),
  };
}

function queryNodeToCondition(
  node: QueryCondition,
  staleAfterMinutes: number,
): DeliveryRunWhenCondition {
  const base: DeliveryRunWhenCondition = {
    id: node.id,
    field: node.field,
    operator: node.operator,
  };

  if (EMPTY_VALUE_OPERATORS.has(node.operator)) {
    return base;
  }

  if (Array.isArray(node.value)) {
    base.values = node.value;
  } else if (node.value !== "") {
    base.value = node.value;
  }

  if (node.operator === "stale_in") {
    base.stale_after_minutes = staleAfterMinutes;
  }

  return base;
}

/**
 * Converts a QueryBuilder value tree back into the Firestore condition shape.
 *
 * @param value - QueryBuilder root group
 * @param staleAfterMinutes - Stale window applied to `stale_in` conditions
 * @returns Firestore condition group
 */
export function queryValueToRunWhen(
  value: QueryGroup,
  staleAfterMinutes: number = DEFAULT_STALE_AFTER_MINUTES,
): DeliveryRunWhenGroup {
  return {
    logic: value.conjunction === "AND" ? "AND" : "OR",
    conditions: value.children.map(
      (child: QueryNode): DeliveryConditionNode =>
        child.type === "group"
          ? queryValueToRunWhen(child, staleAfterMinutes)
          : queryNodeToCondition(child, staleAfterMinutes),
    ),
  };
}
